import { ChangeEvent, FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { toggleFavoriteChannelAction } from '@rikstv/play-common/src/forces/analytics/commonAnalyticsActions';
import { Program } from '@rikstv/play-common/src/forces/channels/channel.types';
import { request } from '@rikstv/play-common/src/forces/utils/request';
import { useAuthToken } from '@rikstv/play-common/src/hooks/useAuthToken';
import { useIsAuthenticated } from '@rikstv/play-common/src/hooks/useIsAuthenticated';
import { Link } from '@rikstv/play-common/src/router/Link';
import { commonRoutes } from '@rikstv/play-common/src/router/router.path';
import { formatTime } from '@rikstv/play-common/src/utils/date/date.utils';
import { Loader, Meta, Tag, ToggleInlineButton } from '@rikstv/shared-components';

import { ProviderImage } from '../../providerImage/ProviderImage';
import { StarIcon } from '../../swimlane/favoriteChannelButton/starIcon';
import { ClampLines } from '../../truncateText/ClampLines';
import { usePrograms } from '../../tvGuide-v2/usePrograms';

import styles from './ChannelOverview.module.css';

export const ChannelOverview = ({
  channel,
  nrOfProgramsToShow = 2,
  className = '',
  currentChannel,
  onClick,
}: {
  channel: RiksTV.ClientAPI.Channel;
  nrOfProgramsToShow?: number;
  className?: string;
  currentChannel?: boolean;
  onClick?: () => void;
}) => {
  const { programs } = usePrograms({ epgUrl: channel._links.epg.href, shouldFetch: true });

  const currentAndFollowingPrograms = programs
    ?.filter(program => {
      const now = new Date();
      const programStart = new Date(program.time);
      // Assuming programs are sorted by time ascending
      return programStart >= now || program.state === 'Live';
    })
    .slice(0, nrOfProgramsToShow); // Get current and next program

  if (currentAndFollowingPrograms?.length === 0) {
    return null;
  }

  return (
    <Link
      style={{ position: 'relative' }}
      onClick={onClick}
      to={commonRoutes.watchLive(channel.channelId, channel.serviceName)}
      className={classNames(styles.channelOverview, className, { [styles.currentChannel]: currentChannel })}>
      <ProviderImageWithLink channel={channel} />

      <div className={classNames(styles.programContainer, { [styles.loader]: !currentAndFollowingPrograms })}>
        {currentAndFollowingPrograms ? (
          currentAndFollowingPrograms.map(program => <ProgramInfo key={program.id} program={program} />)
        ) : (
          <Loader />
        )}
      </div>
      <FavoriteChannelButton
        className={styles.favoriteChannel}
        url={channel._links.myList.href}
        channelId={channel.channelId}
        isDefaultFavorite={!!channel?.isFavorite}
      />
    </Link>
  );
};

const ProgramInfo = ({ program }: { program: Program }) => {
  return (
    <div className={styles.program}>
      <div className={styles.broadcastTime}>
        {program.state === 'Live' ? <Tag>Nå</Tag> : <p>{formatTime(program.time)}</p>}
      </div>
      <ClampLines style={{ textAlign: 'left' }} textStyle={Meta} lines={1}>
        {program.name}
      </ClampLines>
    </div>
  );
};

const ProviderImageWithLink = ({ channel }: { channel: RiksTV.ClientAPI.Channel }) => {
  return (
    <div aria-label={channel.serviceName}>
      <ProviderImage src={`${channel._links.logopack.href}square.svg`} size="large" />
    </div>
  );
};

interface Props {
  className?: string;
  url: string;
  channelId: number;
  isDefaultFavorite: boolean;
}

export const FavoriteChannelButton: FC<Props> = ({ className, url, isDefaultFavorite, channelId }) => {
  const dispatch = useDispatch();
  const [isFavorite, setIsFavorite] = useState(isDefaultFavorite);
  const isLoggedIn = useIsAuthenticated();

  const token = useAuthToken();

  const handleToggleFavoriteChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const newFavorite = e.target.checked;
    setIsFavorite(newFavorite);

    try {
      await request({ url, method: isFavorite ? 'DELETE' : 'POST' }, token);
      dispatch(toggleFavoriteChannelAction({ channelId, isFavorite: newFavorite }));
    } catch (error) {
      setIsFavorite(!newFavorite);
    }
  };

  if (!isLoggedIn) {
    return null;
  }

  const label = isFavorite ? 'Fjern som favorittkanal' : 'Legg til som favorittkanal';
  return (
    <div className={classNames(`${styles.wrapper} ${className}`, { [styles.buttonSolid]: isFavorite })}>
      <ToggleInlineButton
        onChange={e => {
          handleToggleFavoriteChange(e);
        }}
        onClick={e => {
          // Stop click event here to prevent surrounding link to be clicked
          e.preventDefault();
          e.stopPropagation();
        }}
        className={`${styles.button} with-keyboard-focus-within`}
        labelClassName={styles.label}
        inputClassName={styles.input}
        checked={isFavorite}
        aria-live="polite"
        aria-label={label}
        title={label}
        data-context-menu>
        <StarIcon solid={isFavorite} style={{ width: '24px', height: '24px' }} />
      </ToggleInlineButton>
    </div>
  );
};
